import type { ReactElement } from 'react';

import { Divider, LegacySkeleton } from '@/modules/perseo-billing/ui/react/src';

export const PaymentSelectionSkeleton = (): ReactElement => (
  <div className="co-w-full">
    <PaymentSelectionDesktop />
    <PaymentSelectionMobile />
  </div>
);

const Skeleton = ({
  variant = 'rectangular',
  className
}: {
  variant?: 'rectangular' | 'circular';
  className: string;
}) => (
  <div className={`${className} co-rounded-4px`}>
    <LegacySkeleton variant={variant} />
  </div>
);

const PaymentSelectionDesktop = () => (
  <div className="co-hidden lg:co-flex co-w-full co-flex-col co-gap-48px">
    <div className="co-flex co-flex-col co-gap-24px">
      <Title />
      <div className="co-w-full co-flex co-flex-col co-gap-8px">
        <OpenPaymentOption />
        <ClosedPaymentOption />
        <ClosedPaymentOption />
        <ClosedPaymentOption />
      </div>
    </div>
    <ClubParisSkeleton />
  </div>
);

const PaymentSelectionMobile = () => (
  <div className="co-flex lg:co-hidden co-w-full co-flex-col co-gap-24px">
    <div className="co-flex co-flex-col co-gap-16px">
      <Skeleton className="co-w-[100px] co-h-[30px]" />
      <div className="co-flex co-gap-16px">
        <Skeleton className="co-w-[85px] co-h-24px" />
        <Skeleton className="co-w-[85px] co-h-24px" />
      </div>
    </div>

    <div className="co-flex co-flex-col co-gap-8px">
      <MobilePaymentOption />
      <MobilePaymentOption />
      <MobilePaymentOption />
      <MobilePaymentOption />
    </div>
  </div>
);

const Title = () => (
  <div className="co-w-full co-h-[30px] co-flex co-justify-between">
    <Skeleton className="co-w-100px co-h-[30px]" />
    <div className="co-flex co-gap-16px">
      <Skeleton className="co-w-[85px] co-h-[30px]" />
      <Skeleton className="co-w-[85px] co-h-[30px]" />
    </div>
  </div>
);

const OpenPaymentOption = () => (
  <div className="co-w-full co-flex co-flex-col co-gap-16px co-border-1 co-border-[#E6E6E6] co-py-8px co-px-24px co-rounded-8px">
    <div className="co-flex co-justify-between">
      <div className="co-flex co-gap-16px co-items-center">
        <Skeleton variant="circular" className="co-w-40px co-h-40px" />
        <Skeleton className="co-w-[240px] co-h-[24px]" />
      </div>
      <div className="co-flex co-gap-16px co-items-center">
        <Skeleton className="co-w-[70px] co-h-[24px]" />
        <Skeleton variant="circular" className="co-w-24px co-h-24px" />
      </div>
    </div>
    <Divider />
    <Skeleton className="co-w-[242px] co-h-[24px]" />
    <div className="co-w-full co-flex co-gap-24px">
      <div className="co-flex co-flex-col co-w-1/2 co-justify-between">
        <div className="co-h-[44px] co-flex co-rounded-8px co-border-1 co-border-[#B2B2B2] co-p-[12px] co-items-center co-justify-between">
          <Skeleton className="co-w-[173px] co-h-[20px]" />
          <Skeleton variant="circular" className="co-w-20px co-h-20px" />
        </div>

        <div className="co-flex co-flex-col co-gap-24px">
          <div className="co-flex co-justify-between co-items-center">
            <Skeleton className="co-w-[195px] co-h-[24px]" />
            <div className="co-flex co-items-center co-gap-8px">
              <Skeleton variant="circular" className="co-w-20px co-h-20px" />
              <Skeleton className="co-w-[71px] co-h-[24px]" />
            </div>
          </div>
          <Skeleton className="co-w-full co-h-48px co-rounded-full" />
        </div>
      </div>

      <div className="co-flex co-flex-col co-w-1/2 co-rounded-8px co-p-16px co-gap-16px co-bg-pds-neutral-100 co-items-center">
        <div className="co-w-full co-h-[156px] co-flex co-flex-col co-rounded-8px co-p-16px co-gap-16px co-bg-skeleton-card">
          <div className="co-flex co-justify-between">
            <div className="co-flex co-flex-col co-gap-4px">
              <Skeleton className="co-w-48px co-h-32px" />
              <Skeleton className="co-w-[26px] co-h-16px" />
            </div>
            <Skeleton className="co-w-40px co-h-16px" />
          </div>
          <div className="co-flex co-flex-col co-gap-16px">
            <div className="co-flex co-gap-16px">
              <Skeleton className="co-w-[26px] co-h-16px" />
              <Skeleton className="co-w-[26px] co-h-16px" />
              <Skeleton className="co-w-[26px] co-h-16px" />
              <Skeleton className="co-w-[26px] co-h-16px" />
            </div>
            <div className="co-flex co-justify-between">
              <Skeleton className="co-w-[100px] co-h-16px" />
              <Skeleton className="co-w-[50px] co-h-16px" />
            </div>
          </div>
        </div>
        <div className="co-w-full co-flex co-gap-8px co-items-center">
          <Skeleton variant="circular" className="co-w-24px co-h-24px" />

          <Skeleton className="co-w-full co-h-[20px]" />
        </div>
      </div>
    </div>
  </div>
);

const ClosedPaymentOption = () => (
  <div className="co-flex co-border-1 co-border-[#E6E6E6] co-py-8px co-px-24px co-rounded-8px co-justify-between co-items-center">
    <div className="co-flex co-gap-16px co-items-center">
      <Skeleton variant="circular" className="co-w-40px co-h-40px" />
      <Skeleton className="co-w-[240px] co-h-[24px]" />
    </div>
    <Skeleton variant="circular" className="co-w-24px co-h-24px" />
  </div>
);

const MobilePaymentOption = () => (
  <div className="co-flex co-gap-16px co-border-1 co-border-[#E6E6E6] co-p-16px co-rounded-8px co-justify-between co-items-center">
    <div className="co-flex co-gap-16px co-items-center">
      <Skeleton variant="circular" className="co-w-40px co-h-40px" />
      <div className="co-flex co-flex-col co-gap-4px">
        <Skeleton className="co-w-[150px] co-h-24px" />
        <Skeleton className="co-w-[70px] co-h-24px" />
      </div>
    </div>
    <Skeleton variant="circular" className="co-w-24px co-h-24px" />
  </div>
);

const ClubParisSkeleton = () => (
  <div className="co-flex co-flex-col co-gap-24px">
    <Skeleton className="co-flex co-w-100px co-h-32px" />
    <div className="co-flex co-flex-row co-items-center co-justify-between co-rounded-8px co-border-1 co-border-[#E6E6E6] co-py-8px co-px-24px">
      <div className="co-flex co-flex-row co-gap-16px">
        <Skeleton variant="circular" className="co-w-40px co-h-40px" />
        <Skeleton className="co-flex co-w-[240px] co-h-24px co-self-center" />
      </div>
      <Skeleton variant="circular" className="co-w-24px co-h-24px" />
    </div>
  </div>
);
